'use client';

import { useMemo } from 'react';
import { useTheme } from 'next-themes';

const heatMapMetrics = {
  temperature: { name: 'Temperature', unit: '°C', color: '#ef4444', range: [-20, 40] },
  humidity: { name: 'Humidity', unit: '%', color: '#3b82f6', range: [0, 100] },
  airQuality: { name: 'Air Quality', unit: 'AQI', color: '#10b981', range: [0, 100] },
  light: { name: 'Light Level', unit: 'lux', color: '#f59e0b', range: [0, 1500] },
};

interface HeatmapVisualizationProps {
  sensors: any[];
  metric: string;
  enabled: boolean;
}

export default function HeatmapVisualization({ sensors, metric, enabled }: HeatmapVisualizationProps) {
  const { theme } = useTheme();

  const metricConfig = useMemo(() => heatMapMetrics[metric as keyof typeof heatMapMetrics], [metric]);

  const heatmapOpacity = useMemo(() => (theme === 'dark' ? 0.6 : 0.4), [theme]);

  if (!enabled || !metricConfig) return null;

  return (
    <group>
      {sensors.map((sensor) => {
        const value = sensor[metric];
        const normalizedValue = (value - metricConfig.range[0]) / (metricConfig.range[1] - metricConfig.range[0]);
        const intensity = Math.max(0.1, Math.min(0.8, normalizedValue)) * heatmapOpacity;

        return (
          <mesh
            key={sensor.id}
            position={[sensor.position[0], 0.01, sensor.position[2]]}
            rotation={[-Math.PI / 2, 0, 0]}
          >
            <circleGeometry args={[1.2, 32]} />
            <meshBasicMaterial color={metricConfig.color} transparent opacity={intensity} depthWrite={false} />
          </mesh>
        );
      })}
    </group>
  );
}

export { heatMapMetrics };
